import CollectionBase from 'common/base/collection-base';
import {
  BuildingEnum,
  MeterTypeEnum,
  PointScopeEnum,
} from 'common/proxies/enum-proxy';
import UserFacilitiesModel from 'common/models/user-facilities-model';
import {getEnumDictById} from 'common/utils/enum-util';
import {t} from 'common/utils/translation-util';

const TENANT_ID_REGEXP = /^tenant-(\d+)$/i;

export default CollectionBase.extend({
  getCustomFacilityGroup(groupId) {
    const groups = this.getCustomFacilityGroups();
    return groups[groupId];
  },

  _search(
    facilities,
    systemName,
    returnFacility = false,
    pointsField = 'points',
  ) {
    /* Searches an array of arrays for values */
    for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
      const facility = facilities[ii];
      const points = facility[pointsField];
      for (let jj = 0, mm = points.length; jj < mm; jj++) {
        const point = points[jj];
        if (point.systemName === systemName) {
          return returnFacility ? facility : point;
        }
      }
    }
  },

  getCustomFacilityGroups(groupedBy = 'id') {
    if (!this.customFacilityGroups) {
      this.customFacilityGroups = {};
      const facilities = this.getFacilities();
      for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
        const facility = facilities[ii];
        const groups = facility.groups;
        if (groups && groups.length) {
          for (let jj = 0, mm = groups.length; jj < mm; jj++) {
            const group = groups[jj];
            if (!this.customFacilityGroups[group[groupedBy]]) {
              this.customFacilityGroups[group[groupedBy]] = {
                id: group.id,
                name: group.name,
                parent: group.parent,
                facilities: [],
              };
            }
            this.customFacilityGroups[group[groupedBy]].facilities.push(
              facility,
            );
          }
        }
      }
    }
    return this.customFacilityGroups;
  },

  getFacilities() {
    return this.at(0).attributes.results;
  },

  getFacility(urlElement) {
    const facilities = this.getFacilities();
    for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
      const facility = facilities[ii];
      if (facility.urlElement === urlElement) {
        return facility;
      }
    }
  },

  getFacilityForMeter(systemName) {
    const facilities = this.getFacilities();
    for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
      const facility = facilities[ii];
      const points = facility.points;
      for (let jj = 0, mm = points.length; jj < mm; jj++) {
        const point = points[jj];
        if (point.systemName === systemName) {
          return facility;
        }
      }
    }
  },

  getFacilityGroup(facilityType) {
    if (facilityType === BuildingEnum.ALL_FACILITIES.slug) {
      return this.getFacilities();
    }
    return this.getFacilityGroups()[facilityType];
  },

  getFacilityGroups(groupedBy = 'slug') {
    const facilities = this.getFacilities();
    let facilityGroups = this.facilityGroups;
    if (!facilityGroups) {
      facilityGroups = {};
      for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
        const facility = facilities[ii];
        if (!facilityGroups.hasOwnProperty(facility.type[groupedBy])) {
          facilityGroups[facility.type[groupedBy]] = [];
        }
        facilityGroups[facility.type[groupedBy]].push(facility);
      }
      this.facilityGroups = facilityGroups;
    }
    return this.facilityGroups;
  },

  getMeter(systemName) {
    return (
      this._search(this.getFacilities(), systemName) ||
      this._search(this.getTenants(), systemName) ||
      null
    );
  },

  getMetersFor: {
    all() {
      return this.getMetersForFacilityType(undefined);
    },
    building(urlElement) {
      return this.getMetersForFacility(urlElement);
    },
    group(groupId) {
      return this.getMetersForFacilityGroup(groupId);
    },
    type(type) {
      return this.getMetersForFacilityType(type);
    },
    alltenants() {
      return this.getSpacesForTenantType();
    },
    tenant(tenantIdString) {
      // Extract the actual tenant id
      const tenantId = Number(tenantIdString.match(TENANT_ID_REGEXP)[1]);
      return this.getSpacesForTenant(tenantId);
    },
  },

  getMetersForFacility(urlElement) {
    const facilities = this.getFacilities();
    for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
      const facility = facilities[ii];
      if (facility.urlElement === urlElement) {
        return facility.points;
      }
    }
  },

  getMetersForFacilityGroup(groupId) {
    const id = parseInt(groupId, 10);
    const groupHasId = (group) => group.id === id;
    const facilities = this.getFacilities();
    let points = [];
    for (let ii = 0, nn = facilities.length; ii < nn; ii++) {
      const facility = facilities[ii];
      const groups = facility.groups;
      if (groups) {
        if (groups.some(groupHasId)) {
          points = points.concat(facility.points);
        }
      }
    }
    return points;
  },

  getMetersForFacilityType(type) {
    return this.getFacilities()
      .map((facility) => {
        if (facility.type.slug === type || !type) {
          return facility.points;
        }
        return null;
      })
      .filter((meter) => meter)
      .reduce((previous, current) => previous.concat(current));
  },

  getMeterTypes(primaryOnly = false) {
    const primaryTypes = [
      MeterTypeEnum.ELECTRICITY.id,
      MeterTypeEnum.WATER.id,
      MeterTypeEnum.NATURAL_GAS.id,
    ];
    const enumDict = getEnumDictById(MeterTypeEnum);
    return (
      this.getFacilities()
        // Pull all points into a single array of arrays:
        .map((facility) => facility.points)
        // Flatten points array:
        .reduce((previous, current) => previous.concat(current))
        // Create array of meter type IDs:
        .map((point) => {
          if (primaryOnly && primaryTypes.includes(point.type.id)) {
            return point.type.id;
          }
          if (!primaryOnly) {
            return point.type.id;
          }
          return null;
        })
        // Dedupe array of meter type IDs;
        .filter((id, index, arr) => arr.indexOf(id) === index)
        // Filter any dead indexes:
        .filter((id) => id)
        // Replace meter type IDs with meter type enums:
        .map((id) => enumDict[id])
    );
  },

  getOrgUrlElement() {
    const facilities = this.getFacilities();
    if (facilities && facilities.length) {
      if (facilities[0].organization) {
        return facilities[0].organization.urlElement;
      }
    }
  },

  getSelectionCategory(selection) {
    if (selection === 'all' || selection === 'alltenants') {
      return selection;
    }

    if (selection.match(TENANT_ID_REGEXP)) {
      return 'tenant';
    }

    if (this.getFacility(selection)) {
      return 'building';
    }

    if (isNaN(parseInt(selection, 10))) {
      return 'type';
    }

    return 'group';
  },

  getSpacesForTenant(id) {
    const tenants = this.getTenants();
    for (let ii = 0, nn = tenants.length; ii < nn; ii++) {
      const tenant = tenants[ii];
      if (tenant.id === id) {
        // These 'spaces' are actually derived from SpaceVirtualPoints that can
        // Be treated as points elsewhere in the frontend
        return tenant.points;
      }
    }
  },

  getSpacesForTenantType(type) {
    // Presently, there is nothing other than
    if (type) {
      throw new Error('ValueError: tenants cannot yet have types');
    }
    const points = this.getTenants().map((tenant) => tenant.points);

    // Concats all points together
    return [].concat(...points);
  },

  getSubmeters(urlElement) {
    if (urlElement.match(TENANT_ID_REGEXP)) {
      return this.getMetersFor.tenant.call(this, urlElement);
    }
    return this.getMetersForFacility(urlElement);
  },

  getTenantOrFacilityForMeter(systemName) {
    return (
      this._search(this.getFacilities(), systemName, true) ||
      this._search(this.getTenants(), systemName, true) ||
      null
    );
  },

  getTenants() {
    return this.at(0).attributes.tenants;
  },

  getUtilitiesForMeters(meters, onlyWholeBuildingScope = false) {
    const utilities = [];
    const utilityTypes = {};
    const addUtilityType = (type) => {
      utilityTypes[type] = true;
      utilities.push(type);
    };

    if (meters) {
      meters.forEach((meter) => {
        const type = meter.type.slug;
        if (meter.bill && !utilityTypes.hasOwnProperty(type)) {
          if (onlyWholeBuildingScope) {
            if (
              meter.scope === PointScopeEnum.WHOLE ||
              meter.scope === PointScopeEnum.WHOLETENANT
            ) {
              addUtilityType(type);
            }
          } else {
            addUtilityType(type);
          }
        }
      });
    }
    return utilities;
  },

  hasBill(facility) {
    const meters = this.getMetersForFacility(facility.urlElement);
    return meters.some((meter) => meter.bill);
  },

  hasSubmeters(facility) {
    const urlElement = facility.urlElement;
    const meters = this.getSubmeters(urlElement);
    for (let ii = 0, nn = meters.length; ii < nn; ii++) {
      const meter = meters[ii];
      switch (meter.scope.slug) {
        case PointScopeEnum.SUBMETER.slug:
        case PointScopeEnum.SUBBILL.slug:
        case PointScopeEnum.SPACE.slug:
          return true;
        default:
          break;
      }
    }
    return false;
  },

  hasSubmetersOfType(type, facility) {
    const meters = this.getSubmeters(facility);
    for (let ii = 0, nn = meters.length; ii < nn; ii++) {
      const meter = meters[ii];
      if (meter.type.slug === type.value) {
        switch (meter.scope.slug) {
          case PointScopeEnum.SUBMETER.slug:
          case PointScopeEnum.SUBBILL.slug:
          case PointScopeEnum.SPACE.slug:
            return true;
          default:
            break;
        }
      }
    }
    return false;
  },

  initialize() {
    this.errorMessage = t('Your buildings failed to load.');
    this.tenants = [];
    this.facilityGroups = null;
    this.customFacilityGroups = null;
    this.model = UserFacilitiesModel;
  },
});
